import { useMutation, useQuery } from 'react-query';
import { isObject } from 'lodash';

import { FormActions, NotificationPosition } from 'coreOld/enums';
import { useInfiniteQuery, useHandleErrors } from 'coreOld/hooks';
import { GetOneResult } from 'coreOld/types';
import { actionLoadingMessage, actionMessage } from 'coreOld/utils';
import { companyPartsService } from 'partsOld/services';
import { ICompanyPart, ICompanyPartRequestPayload } from 'partsOld/types';

export const useGetCompanyParts = (requestPayload?: ICompanyPartRequestPayload) =>
  useInfiniteQuery<ICompanyPart>(companyPartsService.getMany, 'companyParts', requestPayload);

export const useGetCompanyPartsCount = (requestPayload?: ICompanyPartRequestPayload) => {
  const { data, isError, isLoading } = useQuery(
    ['companyPartsCount', requestPayload],
    ({ signal }) => companyPartsService.getManyCount(requestPayload, signal)
  );

  return {
    count: data?.count ?? 0,
    isError,
    isLoading,
  };
};

export const useGetCompanyPart = (queryParams, enabled?): GetOneResult<Partial<ICompanyPart>> => {
  const { data, isLoading, isError, refetch } = useQuery(
    ['companyPart', queryParams],
    () => companyPartsService.getOne(queryParams),
    {
      enabled: Boolean(enabled),
    }
  );

  const companyPart = isObject(data?.record) ? data?.record : null;

  return {
    record: companyPart ?? null,
    isLoading,
    isError,
    refetch,
  };
};

export const useCompanyPartsActions = (notificationPosition?: NotificationPosition) => ({
  ...useUpsertCompanyPart(notificationPosition),
  ...useExportCompanyParts(),
  ...useRemoveBulkCompanyParts(),
  ...useAddCostNote(),
  ...useEditCostNote(),
  ...useRemoveCostNote(),
});

const useUpsertCompanyPart = (notificationPosition = NotificationPosition.TAB) => {
  const { errors } = useHandleErrors(
    actionMessage('Company part'),
    actionLoadingMessage('company part'),
    notificationPosition,
    ['companyPart', 'companyParts'],
    FormActions.UPSERT
  );

  const { mutateAsync, isLoading } = useMutation(companyPartsService.upsertCompanyPart, {
    ...errors,
  });

  const upsertCompanyPart = (data) => mutateAsync(data);

  return { upsertCompanyPart, upsertIsLoading: isLoading };
};

const useExportCompanyParts = () => {
  const { mutateAsync } = useMutation(companyPartsService.exportCompanyParts);

  const exportCompanyParts = (data) => mutateAsync(data);

  return { exportCompanyParts };
};

const useRemoveBulkCompanyParts = () => {
  const { errors } = useHandleErrors(
    actionMessage('Part'),
    actionLoadingMessage('part'),
    NotificationPosition.MODAL,
    ['companyParts'],
    FormActions.REMOVE
  );

  const { mutateAsync } = useMutation(companyPartsService.removeBulk, {
    ...errors,
  });

  const removebulk = (data) => mutateAsync(data);

  return { removebulk };
};

const useAddCostNote = () => {
  const { errors } = useHandleErrors(
    actionMessage('Note'),
    actionLoadingMessage('note'),
    NotificationPosition.MODAL,
    ['companyParts'],
    FormActions.ADD
  );

  const { mutateAsync } = useMutation(companyPartsService.addCostNote, {
    ...errors,
  });

  const addCostNote = (data) => mutateAsync(data);

  return { addCostNote };
};

const useRemoveCostNote = () => {
  const { errors } = useHandleErrors(
    actionMessage('Note'),
    actionLoadingMessage('note'),
    NotificationPosition.MODAL,
    ['companyParts'],
    FormActions.REMOVE
  );

  const { mutateAsync } = useMutation(companyPartsService.removeCostNote, {
    ...errors,
  });

  const removeCostNote = (data) => mutateAsync(data);

  return { removeCostNote };
};

const useEditCostNote = () => {
  const { errors } = useHandleErrors(
    actionMessage('Note'),
    actionLoadingMessage('note'),
    NotificationPosition.MODAL,
    [],
    FormActions.UPDATE
  );

  const { mutateAsync } = useMutation(companyPartsService.editCostNote, {
    ...errors,
  });

  const editCostNote = (data) => mutateAsync(data);

  return { editCostNote };
};
